import axios from 'axios';

const adminUrl = process.env.VUE_APP_ADMIN_URL;

function headers() {
  const accessToken = JSON.parse(localStorage.getItem('accessToken'));
  return { Authorization: `Bearer ${accessToken}` };
}

/** Saca los filtros vacíos: el backend toma su default cuando un parámetro no viene. */
function cleanParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '')
  );
}

// El backend de /b2c-report-v2 responde { data, meta } / { error: { code, message } };
// aquí se traduce al { status, data } que usa el resto del admin.
export default class B2CReportV2Service {
  /**
   * Foto a una fecha de corte (specs/090-b2c-report-v2, GET /snapshot).
   *
   * @param {{ date?: string, country?: string }} filters
   */
  static async getSnapshot({ date, country } = {}) {
    try {
      const response = await axios.get(`${adminUrl}/b2c-report-v2/snapshot`, {
        params: cleanParams({ date, country }),
        headers: headers()
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Error al cargar la foto B2C';
      return { status: 'error', message };
    }
  }

  /**
   * Altas, bajas, neto y stock de cada mes del año (GET /trend).
   *
   * @param {{ year?: number, country?: string }} filters
   */
  static async getTrend({ year, country } = {}) {
    try {
      const response = await axios.get(`${adminUrl}/b2c-report-v2/trend`, {
        params: cleanParams({ year, country }),
        headers: headers()
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Error al cargar la tendencia';
      return { status: 'error', message };
    }
  }

  /**
   * Membresías activas de una caja (GET /detail).
   *
   * @param {{ box: string, contract?: string, group?: string, date?: string, country?: string }} filters
   *   `contract`: código de contrato, o 'none' para las membresías sin convenio.
   *   `group`: 'direct' para la fila "Directo" de B2C.
   */
  static async getDetail({ box, contract, group, date, country }) {
    try {
      const response = await axios.get(`${adminUrl}/b2c-report-v2/detail`, {
        params: cleanParams({ box, contract, group, date, country }),
        headers: headers()
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Error al cargar las membresías';
      return { status: 'error', message };
    }
  }

  /**
   * Altas y bajas de un mes del gráfico, de B2C, B2B2C y Upsell (GET /trend/detail).
   *
   * @param {{ month: string, bciOnly?: boolean, country?: string }} filters
   */
  static async getTrendDetail({ month, bciOnly, country }) {
    try {
      const response = await axios.get(`${adminUrl}/b2c-report-v2/trend/detail`, {
        params: cleanParams({ month, bciOnly: bciOnly ? 'true' : 'false', country }),
        headers: headers()
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Error al cargar las membresías del mes';
      return { status: 'error', message };
    }
  }

  /**
   * MRR, activas, altas y bajas de cada día de un mes, del total B2C/B2B2C (spec 129, GET /daily).
   *
   * @param {{ month?: string, country?: string }} filters  `month` como 'YYYY-MM'.
   */
  static async getDaily({ month, country } = {}) {
    try {
      const response = await axios.get(`${adminUrl}/b2c-report-v2/daily`, {
        params: cleanParams({ month, country }),
        headers: headers()
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Error al cargar el MRR diario';
      return { status: 'error', message };
    }
  }

  /**
   * Membresías de un día: altas, bajas y vigentes (GET /daily/detail).
   *
   * @param {{ date: string, country?: string }} filters  `date` como 'YYYY-MM-DD'.
   */
  static async getDailyDetail({ date, country }) {
    try {
      const response = await axios.get(`${adminUrl}/b2c-report-v2/daily/detail`, {
        params: cleanParams({ date, country }),
        headers: headers()
      });
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Error al cargar las membresías del día';
      return { status: 'error', message };
    }
  }

  /**
   * Guarda por qué se entregó una membresía de Pawer Loyalty (PATCH /memberships/:id/note).
   * Una nota vacía la borra.
   *
   * @param {string} membershipId
   * @param {string|null} note
   */
  static async updateLoyaltyNote(membershipId, note) {
    try {
      const response = await axios.patch(
        `${adminUrl}/b2c-report-v2/memberships/${membershipId}/note`,
        { note },
        { headers: headers() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'No se pudo guardar el comentario';
      return { status: 'error', message };
    }
  }
}
